import { Col, Row } from "antd";
import { useFormik } from "formik";
import { isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import { ATTACHMENT_TYPES } from "utils/constants";
import { downloadUploadedDocument } from "utils/helpers/files";
import ActionButtons from "../Components/ActionButtons";
import Header from "../Components/Header";
import Layout from "../Components/Layout";
import DocumentUpload from "./DocumentUpload";
import validation from "./validation";

function View({
  initialValues,
  onSubmit,
  downloadFile,
  onBack,
  supportingDocuments,
  isRia,
  onFileRemove: handleFileRemove,
}) {
  const isTax = supportingDocuments.includes(ATTACHMENT_TYPES.PAY_SLIP);
  const isProfessional = supportingDocuments.includes(
    ATTACHMENT_TYPES.PROFESSIONAL_PAYMENT_ACCOUNT,
  );
  const isEnrollmentCertificate = supportingDocuments.includes(
    ATTACHMENT_TYPES.PROFESSIONAL_PAYMENT_ACCOUNT,
  );
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const { isValid, handleSubmit, setFieldValue, values, ...formProps } =
    useFormik({
      initialValues,
      enableReinitialize: true,
      onSubmit: onSubmit,
      validationSchema: validation(
        t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, {
          returnObjects: true,
        }),
        { isTax, isRia, isProfessional, isEnrollmentCertificate },
      ),
    });

  const createFileRemoveHandler = (fieldName) => async (item) => {
    if (handleFileRemove) {
      await handleFileRemove(fieldName)(item);
      // Remove file from form values immediately
      const currentFiles = values[fieldName] || [];
      const updatedFiles = currentFiles.filter(
        (file) => (file?.uid || file?.id) !== (item?.uid || item?.id),
      );
      setFieldValue(fieldName, updatedFiles);
    }
  };

  const handleDownload = ({ uid, size, base64, contentType, name }) => {
    if (!size) return downloadFile(uid);
    downloadUploadedDocument({
      base64,
      contentType,
      name,
    });
  };

  return (
    <Layout>
      <Header
        title="Dernière chose ! Nous aurons besoin de vos pièces justificatives"
        highlight="vos pièces justificatives"
        subtitle="Pièces justificatives"
      />
      <Row justify="center">
        <Col xs={24} md={16} lg={14} xl={12}>
          <div className="amellis__documents--container">
            <DocumentUpload
              label={"Relevé d'Identité Bancaire (RIB)"}
              items={values.paymentAccount}
              onDownload={handleDownload}
              onUpload={(item) => {
                setFieldValue("paymentAccount", item);
              }}
              onFileRemove={createFileRemoveHandler("paymentAccount")}
              required={!isProfessional}
            />
            <DocumentUpload
              label={"Carte d'identité nationale"}
              items={values.cin}
              onDownload={handleDownload}
              onUpload={(item) => {
                setFieldValue("cin", item);
              }}
              onFileRemove={createFileRemoveHandler("cin")}
              required
            />
            <DocumentUpload
              label={"Attestation de sécurité sociale"}
              items={values.socialSecurityCertificate}
              onDownload={handleDownload}
              onUpload={(item) => {
                setFieldValue("socialSecurityCertificate", item);
              }}
              onFileRemove={createFileRemoveHandler(
                "socialSecurityCertificate",
              )}
              required
            />
            {isProfessional && (
              <DocumentUpload
                label={"Rib Professionnel"}
                items={values.professionalPaymentAccount}
                onDownload={handleDownload}
                onUpload={(item) => {
                  setFieldValue("professionalPaymentAccount", item);
                }}
                onFileRemove={createFileRemoveHandler(
                  "professionalPaymentAccount",
                )}
                required={true}
              />
            )}
            {isTax && (
              <DocumentUpload
                label={
                  "Justificatif bulletin de salaire ou certificat employeur"
                }
                items={values.paySlip}
                onDownload={handleDownload}
                onUpload={(item) => {
                  setFieldValue("paySlip", item);
                }}
                onFileRemove={createFileRemoveHandler("paySlip")}
                required={false}
              />
            )}
            {isRia && (
              <DocumentUpload
                label={"Pouvoir en signature électronique"}
                items={values.electronicSignatureEligibility}
                onDownload={handleDownload}
                onUpload={(item) => {
                  setFieldValue("electronicSignatureEligibility", item);
                }}
                onFileRemove={createFileRemoveHandler(
                  "electronicSignatureEligibility",
                )}
                required={true}
              />
            )}
            <DocumentUpload
              label={"Autres documents"}
              items={values.supplementaryDocuments || []}
              onDownload={handleDownload}
              onUpload={(newItems) => {
                const existingItems = values.supplementaryDocuments || [];
                // Merge existing items with new items, avoiding duplicates
                const mergedItems = [...existingItems];
                newItems.forEach((newItem) => {
                  const exists = mergedItems.some(
                    (existing) =>
                      (existing?.uid || existing?.id) ===
                      (newItem?.uid || newItem?.id),
                  );
                  if (!exists) {
                    mergedItems.push(newItem);
                  }
                });
                setFieldValue("supplementaryDocuments", mergedItems);
              }}
              onFileRemove={createFileRemoveHandler("supplementaryDocuments")}
              required={false}
              multiple={true}
            />
            <div className="recap__wrapper--info">
              <p>Formats de fichiers pris en charge : JPEG, PNG ou PDF</p>
              <p>Taille maximale : 5 Mo</p>
            </div>
          </div>
        </Col>
      </Row>
      <ActionButtons
        disabled={!isEmpty(formProps.errors) || isEmpty(values, "cin")}
        onNext={handleSubmit}
      />
    </Layout>
  );
}

export default View;
